/**
 * Good to Start watcher notices (relay `services/gtsStartWatcher.ts`).
 *
 * When Kalshi's start moves EARLIER than the expiry a resting Good to Start
 * order carries, the relay cancels that order at the new kickoff. The order
 * then simply vanishes from Open Orders, so this module fetches the trader's
 * own recent watcher cancels and the game card prints a plain line where the
 * order used to rest:
 *
 *   ARIZ3 YES 45¢ ×5000 — Good to Start: cancelled at kickoff (start moved to 8:30a)
 *
 * One shared poller for every mounted card (never one request per card). It
 * polls only while a card is mounted and the tab is visible; when the relay
 * says the watcher is off (or predates the route) it backs off to a slow
 * re-check, and that cadence survives every card unmounting, so re-opening
 * Game Detail never forces an early read. An unchanged response re-renders
 * nothing. Notices match a card by sport and event code together. Every
 * failure is "no data", never a throw.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { apiFetch } from '@/stores/authStore';
import { formatStartTimeLocal } from '@/stores/marketStore';
import { sportFromTicker } from '@/lib/kalshiTicker/sportFilter';

export interface GoodToStartNotice {
  kalshiOrderId: string;
  ticker: string;
  side: string | null;
  count: number | null;
  priceDollars: string | null;
  reason: 'start_moved_earlier';
  /** The authoritative start that triggered the cancel (epoch ms). */
  startMs: number;
  /** The start the order was placed against (epoch ms). */
  placedStartMs: number;
  cancelledAt: number;
  /** `cancelled`: the venue confirmed the watcher's cancel.
   *  `gone_after_retry`: a watcher cancel failed and its retry found the
   *  order already gone (that attempt may have landed, or the order filled). */
  outcome: 'cancelled' | 'gone_after_retry';
}

const NOTICES_URL = '/api/kalshi/gts-watcher/notices';
const POLL_MS = 15_000;
const DISABLED_POLL_MS = 5 * 60_000;
/** Same window the relay serves; re-applied at each fetch. */
export const GOOD_TO_START_NOTICE_WINDOW_MS = 30 * 60_000;

function finiteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Validate AND project: the result is rebuilt from the exact rendered
 *  fields, so nothing else a relay might add can reach the DOM. */
function parseNotice(raw: unknown): GoodToStartNotice | null {
  if (raw == null || typeof raw !== 'object') return null;
  const n = raw as Record<string, unknown>;
  if (typeof n.kalshiOrderId !== 'string' || typeof n.ticker !== 'string') return null;
  if (n.reason !== 'start_moved_earlier') return null;
  if (!finiteNumber(n.startMs) || !finiteNumber(n.placedStartMs) || !finiteNumber(n.cancelledAt)) return null;
  // A relay that predates the field only served confirmed cancels.
  const outcome = n.outcome === undefined ? 'cancelled' : n.outcome;
  if (outcome !== 'cancelled' && outcome !== 'gone_after_retry') return null;
  return {
    kalshiOrderId: n.kalshiOrderId,
    ticker: n.ticker,
    side: typeof n.side === 'string' ? n.side : null,
    count: finiteNumber(n.count) ? n.count : null,
    priceDollars: typeof n.priceDollars === 'string' ? n.priceDollars : null,
    reason: 'start_moved_earlier',
    startMs: n.startMs,
    placedStartMs: n.placedStartMs,
    cancelledAt: n.cancelledAt,
    outcome,
  };
}

export function parseGoodToStartNotices(body: unknown): { enabled: boolean; notices: GoodToStartNotice[] } {
  if (body == null || typeof body !== 'object') return { enabled: false, notices: [] };
  const b = body as Record<string, unknown>;
  const enabled = b.enabled === true;
  if (!enabled || !Array.isArray(b.notices)) return { enabled, notices: [] };
  return {
    enabled,
    notices: b.notices.map(parseNotice).filter((n): n is GoodToStartNotice => n != null),
  };
}

/** The plain line: what happened and the start it moved to, in the app's
 *  local clock format. */
export function goodToStartNoticeLine(n: GoodToStartNotice): string {
  const start = formatStartTimeLocal(n.startMs);
  return n.outcome === 'gone_after_retry'
    ? `Good to Start: no longer resting after a kickoff cancel attempt (start moved to ${start})`
    : `Good to Start: cancelled at kickoff (start moved to ${start})`;
}

/** Which order: market suffix, side, price and size, as far as known. */
export function goodToStartOrderLabel(n: GoodToStartNotice): string {
  const market = n.ticker.slice(n.ticker.lastIndexOf('-') + 1);
  const parts = [market];
  if (n.side) parts.push(n.side.toUpperCase());
  const dollars = n.priceDollars != null ? Number.parseFloat(n.priceDollars) : NaN;
  if (Number.isFinite(dollars)) parts.push(`${Math.round(dollars * 10_000) / 100}¢`);
  if (n.count != null) parts.push(`×${n.count}`);
  return parts.join(' ');
}

/** `KXNCAAFSPREAD-26OCT10ARIZWVU-ARIZ3` → `26OCT10ARIZWVU`: the event code
 *  shared by every market (moneyline, spread, total, …) of one game. */
export function kalshiGameCode(ticker: string | null | undefined): string | null {
  if (!ticker) return null;
  const parts = ticker.split('-');
  return parts.length >= 3 && parts[1] ? parts[1] : null;
}

/** `sport|eventCode` (`cfb|26OCT10ARIZWVU`): one game across all its markets.
 *  The event code alone can repeat across sports on the same date (college
 *  football and basketball share team codes), so a notice matches a card only
 *  on both. An unrecognised sport keys as `|code` on both sides. */
export function goodToStartGameKey(ticker: string | null | undefined): string | null {
  const code = kalshiGameCode(ticker);
  if (code == null || !ticker) return null;
  return `${sportFromTicker(ticker) ?? ''}|${code}`;
}

// ── Shared poller ─────────────────────────────────────────────────────────

let notices: GoodToStartNotice[] = [];
let subscribers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let generation = 0;
/** Earliest time the next read may start: the last read's cadence (15 s, or
 *  5 min while the watcher is off or the route is missing) survives every
 *  card unmounting, so re-opening Game Detail does not bypass it. */
let nextAllowedAt = 0;
const listeners = new Set<() => void>();

/** Notices are projected with a fixed field order, so equal content
 *  serializes equally. */
function sameNotices(a: readonly GoodToStartNotice[], b: readonly GoodToStartNotice[]): boolean {
  return a.length === b.length && JSON.stringify(a) === JSON.stringify(b);
}

/** Publish only a real change: an unchanged poll re-renders nothing. */
function emit(next: GoodToStartNotice[]) {
  if (sameNotices(notices, next)) return;
  notices = next;
  for (const l of listeners) l();
}

function withinWindow(list: GoodToStartNotice[]): GoodToStartNotice[] {
  const cutoff = Date.now() - GOOD_TO_START_NOTICE_WINDOW_MS;
  return list.filter((n) => n.cancelledAt >= cutoff);
}

function schedule(delayMs: number, gen: number) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void load(gen), delayMs);
}

async function load(gen: number): Promise<void> {
  if (gen !== generation || subscribers === 0) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    schedule(POLL_MS, gen);
    return;
  }
  let delay = POLL_MS;
  nextAllowedAt = Date.now() + POLL_MS;
  try {
    const res = await apiFetch(NOTICES_URL);
    if (!res.ok) {
      delay = DISABLED_POLL_MS; // an older relay without the route, or a failure
      nextAllowedAt = Date.now() + delay;
      if (gen !== generation) return;
    } else {
      const parsed = parseGoodToStartNotices(await res.json());
      if (!parsed.enabled) delay = DISABLED_POLL_MS;
      nextAllowedAt = Date.now() + delay;
      if (gen !== generation) return;
      // Aged out here, at fetch time, so render stays pure; a notice lingers
      // at most one poll past the window.
      emit(withinWindow(parsed.notices));
    }
  } catch {
    // keep the last known notices; the next poll retries
  }
  if (gen === generation && subscribers > 0) schedule(delay, gen);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function acquire(): () => void {
  subscribers += 1;
  if (subscribers === 1) {
    generation += 1;
    // Notices kept from an earlier mount age out before they show again.
    emit(withinWindow(notices));
    const wait = nextAllowedAt - Date.now();
    if (wait > 0) schedule(wait, generation);
    else void load(generation);
  }
  return () => {
    subscribers -= 1;
    if (subscribers === 0) {
      generation += 1;
      if (timer) clearTimeout(timer);
      timer = null;
    }
  };
}

/**
 * Recent watcher cancels on the given games (by goodToStartGameKey), newest
 * first. Empty while the watcher is off, loading, or nothing applies.
 */
export function useGoodToStartNotices(gameKeys: readonly string[]): GoodToStartNotice[] {
  // Keys are `sport|code` (letters, digits, one pipe), so a comma joins them.
  const key = [...new Set(gameKeys)].sort().join(',');
  useEffect(() => (key === '' ? undefined : acquire()), [key]);
  const all = useSyncExternalStore(subscribe, () => notices, () => notices);
  return useMemo(() => {
    if (key === '') return [];
    const keys = new Set(key.split(','));
    return all.filter((n) => {
      const k = goodToStartGameKey(n.ticker);
      return k != null && keys.has(k);
    });
  }, [all, key]);
}

export function _resetGoodToStartNoticesForTests(): void {
  generation += 1;
  if (timer) clearTimeout(timer);
  timer = null;
  subscribers = 0;
  nextAllowedAt = 0;
  notices = [];
  listeners.clear();
}
